import { PendingButton } from "@/components/common/PendingButton/PendingButton"
import { toast } from "@/components/common/Toast"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { Typography } from "@/components/ui/typography"
import { toastApiError } from "@/features/material-compliance/api/apiError"
import { useObjectUrl } from "@/hooks/useObjectUrl"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  useDeleteOrgSignature,
  useOrgSignatureImage,
  useOrgSignatures,
  type OrganizationSignature,
} from "@/services/api/organizationSignaturesApi"
import { Plus, Trash2 } from "lucide-react"
import { lazy, Suspense, useState } from "react"
import {
  SettingsCard,
  SettingsCardGrid,
  SettingsCardMedia,
  SettingsCardMenu,
} from "../../components/SettingsCard"
import { SettingsPage, SettingsSection } from "../../components/SettingsPage"
import {
  SettingsLoadError,
  SettingsSkeleton,
} from "../../components/SettingsStates"

// Pulls react-signature-canvas, react-image-crop and the bundled Caveat
// webfont, so it only loads once the user opens it.
const AddSignatureDialog = lazy(
  () => import("@/components/feature/Settings/signatures/AddSignatureDialog"),
)

function SignatureCard({ signature }: { signature: OrganizationSignature }) {
  const { translate } = useTranslation()
  const { data: blob } = useOrgSignatureImage(signature.id)
  const imageUrl = useObjectUrl(blob)
  const deleteSignature = useDeleteOrgSignature()
  const [confirming, setConfirming] = useState(false)

  async function onDelete() {
    try {
      await deleteSignature.mutateAsync(signature.id)
      setConfirming(false)
      toast.success(translate("MC_ORG_SIG_TOAST_DELETED"))
    } catch (error) {
      toastApiError(error, translate, "MC_ORG_SIG_TOAST_DELETE_ERR")
    }
  }

  return (
    <>
      <SettingsCard
        title={signature.signatoryName}
        meta={signature.signatoryFunction ?? undefined}
        media={
          <SettingsCardMedia>
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={signature.signatoryName}
                className="max-h-20 max-w-[85%] object-contain"
              />
            ) : (
              <Spinner className="text-muted-foreground" />
            )}
          </SettingsCardMedia>
        }
        menu={
          <SettingsCardMenu
            label={translate("SETTINGS/SIGNATURES/MENU")}
            items={[
              {
                label: translate("MC_ORG_SIG_DELETE_CONFIRM_OK"),
                icon: Trash2,
                destructive: true,
                onSelect: () => setConfirming(true),
              },
            ]}
          />
        }
      />

      <AlertDialog
        open={confirming}
        onOpenChange={(open) =>
          !deleteSignature.isPending && setConfirming(open)
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {translate("MC_ORG_SIG_DELETE_CONFIRM_TITLE")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {translate("MC_ORG_SIG_DELETE_CONFIRM_MESSAGE", {
                name: signature.signatoryName,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSignature.isPending}>
              {translate("MC_MD_EDIT_CANCEL")}
            </AlertDialogCancel>
            <PendingButton
              variant="destructive"
              pending={deleteSignature.isPending}
              onClick={onDelete}
            >
              {translate("MC_ORG_SIG_DELETE_CONFIRM_OK")}
            </PendingButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/** The current user's own signature images. Per user: the API only ever
 *  lists the caller's signatures, a colleague's never show here. */
export function SignaturesPage() {
  const { translate } = useTranslation()
  const {
    data: signatures,
    isLoading,
    isFetching,
    refetch,
  } = useOrgSignatures()
  const [addOpen, setAddOpen] = useState(false)

  return (
    <SettingsPage
      title={translate("SETTINGS/SIGNATURES/TITLE")}
      description={translate("SETTINGS/SIGNATURES/DESCRIPTION")}
      notice={translate("SETTINGS/SIGNATURES/NOTICE")}
      action={
        <Button onClick={() => setAddOpen(true)}>
          <Plus />
          {translate("MC_ORG_SIG_ADD")}
        </Button>
      }
    >
      <SettingsSection target="signatures">
        {/* Cached data first: a failed background refetch must not close an
            open delete confirmation. */}
        {!signatures ? (
          isLoading ? (
            <SettingsSkeleton rows={2} />
          ) : (
            <SettingsLoadError
              pending={isFetching}
              onRetry={() => void refetch()}
            />
          )
        ) : !signatures.length ? (
          <div className="flex flex-col items-center gap-1 rounded-md bg-accent px-6 py-10 text-center">
            <Typography as="small">
              {translate("MC_ORG_SIG_EMPTY_TITLE")}
            </Typography>
            <Typography as="muted">
              {translate("MC_ORG_SIG_EMPTY_BODY")}
            </Typography>
          </div>
        ) : (
          <SettingsCardGrid>
            {signatures.map((signature) => (
              <SignatureCard key={signature.id} signature={signature} />
            ))}
          </SettingsCardGrid>
        )}
      </SettingsSection>

      {/* Mounted only while open: the lazy chunk loads on first open and the
          dialog's own state (name, tab, drawing) starts fresh every time. */}
      {addOpen && (
        <Suspense fallback={null}>
          <AddSignatureDialog open={addOpen} onOpenChange={setAddOpen} />
        </Suspense>
      )}
    </SettingsPage>
  )
}
